'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useApp } from '@/forApp/states/appState/appState';
import type { ConfigItemT } from '@/forApp/types/Config';
import fetcher from '@/forApp/utils/fetcher/fetcher';
import Breadcrumbs from '@/shared/Breadcrumbs';
import Button from '@/shared/Buttons/Button';
import Checkbox from '@/shared/Checkboxs/Checkbox';
import Input from '@/shared/Input/input';
import ContactsBanner from '@/widgets/Banners/ContactsBanner/ContactsBanner';
import ContactsCard from '@/widgets/Contacts/ContactsCard/ContactsCard';
import { ROUTES } from '@/forApp/utils/routes';

import styles from './styles.module.css';

// Breadcrumbs data
const crumbs = [{ link: '/', title: 'Главная' }, { title: 'Контакты' }];

// Form data interface
interface FormData {
  name: string;
  phone: string;
  email: string;
  from: string;
}

interface ContactsPageClientProps {
  contactCards: ConfigItemT[];
}

const ContactsPageClient: React.FC<ContactsPageClientProps> = ({
  contactCards,
}) => {
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const { addNotification } = useApp();
  const pathname = usePathname();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      from: pathname,
    },
    mode: 'onChange',
  });

  // Form submission handler
  const onSubmit = async (data: FormData) => {
    if (!policyAgreed) {
      return;
    }

    try {
      await fetcher<{ success: boolean }>('POST', '/feedbacks', {
        body: JSON.stringify(data),
      });
      reset();
      setPolicyAgreed(false);
      addNotification({
        text: 'Запрос на консультацию отправлен!',
        type: 'success',
      });
    } catch (error) {
      console.error('Error sending feedback:', error);
      addNotification({
        text: 'Ошибка при отправке сообщения',
        type: 'error',
        id: Date.now().toString(),
      });
    }
  };

  return (
    <div className={`${styles.mainWrap} ${styles.contactsPage}`}>
      <div className={styles.breadcrumbsWrp}>
        <Breadcrumbs crumbs={crumbs} />
      </div>
      <div className="container">
        <div className={styles.contactsMainbanner}>
          <ContactsBanner>
            <form
              className={styles.formWrapper}
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className={styles.formField}>
                <Input
                  name="name"
                  control={control}
                  rules={{ required: 'Введите ваше имя' }}
                  placeholder="Ваше имя"
                />
              </div>

              <div className={styles.formField}>
                <Input
                  name="phone"
                  control={control}
                  rules={{ required: 'Введите номер телефона' }}
                  placeholder="Телефон"
                  mask="+7 (___) ___ __ __"
                />
              </div>

              <div className={styles.formField}>
                <Input
                  name="email"
                  control={control}
                  rules={{
                    required: 'Введите email',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Некорректный email',
                    },
                  }}
                  placeholder="Почта"
                />
              </div>

              <Button
                type="submit"
                color="white"
                text="Отправить"
                disabled={!policyAgreed || !isValid}
              />

              <div className={styles.policyWrapper}>
                <Checkbox
                  isChecked={policyAgreed}
                  onChange={() => setPolicyAgreed(!policyAgreed)}
                />
                <span>
                  Я согласен(а) с условиями{' '}
                  <Link
                    href={ROUTES.POLICY}
                    target="_blank"
                    style={{ color: 'white' }}
                  >
                    Политики конфиденциальности
                  </Link>{' '}
                  и даю согласие на обработку персональных данных.{' '}
                  <span style={{ color: 'red' }}>*</span>
                </span>
              </div>
            </form>
          </ContactsBanner>

          <div className={styles.contactsCards}>
            {contactCards.map((card) => (
              <ContactsCard key={card.code} card={card} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsPageClient;
